"use client";

import { motion, useReducedMotion } from "framer-motion";
export default function Values() {
  const prefersReducedMotion = useReducedMotion();
  const ingredients = [{
    label: "Principle / 01",
    titleStart: "Direct ",
    titleItalic: "access",
    titleEnd: ".",
    description: "You work directly with me — the person analysing, strategising and optimising your SEO.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/direct_access.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/direct_access.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/direct_access.webp-wvc-sizes",
    alt: "Yassine Essaadi working at a clean desk with a laptop and notebook"
  }, {
    label: "Principle / 02",
    titleStart: "Strategic ",
    titleItalic: "SEO",
    titleEnd: ".",
    description: "Every recommendation is based on your business goals, search demand, competitors and customer intent.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/tailored_strategy.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/tailored_strategy.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/tailored_strategy.png-wvc-sizes",
    alt: "Detailed SEO strategy document with charts and growth projections"
  }, {
    label: "Principle / 03",
    titleStart: "Complete ",
    titleItalic: "transparency",
    titleEnd: ".",
    description: "You'll understand what I'm doing, why I'm doing it and how SEO performance is progressing.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/clear_results.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/clear_results.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/clear_results.webp-wvc-sizes",
    alt: "Digital dashboard showing positive SEO growth trends and analytics"
  }, {
    label: "Principle / 04",
    titleStart: "Focus on ",
    titleItalic: "results",
    titleEnd: ".",
    description: "I focus on qualified traffic, enquiries, sales opportunities and measurable organic growth — not vanity metrics.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/focus_on_results.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/focus_on_results.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/focus_on_results.webp-wvc-sizes",
    alt: "Analytics report highlighting organic conversion growth and business performance metrics"
  }];
  return <section data-section-id="30640" id="my-core-values" data-nav="light" className="bg-background py-28 md:py-40 text-foreground">
      <div className="mx-auto w-full max-w-[1440px] px-[clamp(1.25rem,4vw,3rem)]">
        <div className="grid grid-cols-1 md:grid-cols-12 md:gap-x-[clamp(2rem,5vw,5.5rem)]">
          <div className="md:col-span-3">
            <span className="font-sans text-[clamp(0.8rem,0.9vw,0.9375rem)] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              My consulting promise
            </span>
          </div>

          <div className="mt-6 md:col-span-9 md:mt-0">
            <h2 className="font-serif font-normal text-foreground" style={{
            fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
            letterSpacing: "-0.03em",
            lineHeight: 0.98
          }}>
              Why work{" "}
              <span className="relative inline-block italic">
                <span className="relative z-10">directly</span>
                <svg aria-hidden="true" viewBox="0 0 300 40" preserveAspectRatio="none" className="absolute left-[-2%] right-[-2%] bottom-[8%] z-0 h-[42%] w-[104%]" style={{
                color: "hsl(220, 80%, 30%)"
              }}>
                  <path d="M4 28 C 60 12, 140 8, 210 18 S 280 26, 296 20 L 294 34 C 220 40, 130 36, 60 34 S 10 32, 4 28 Z" fill="currentColor" fillOpacity="0.4" />
                </svg>
              </span>
              .
            </h2>

            <p className="mt-8 max-w-2xl font-sans text-[1.25rem] leading-[1.55] text-muted-foreground">
              I provide hands-on SEO expertise for businesses that value transparency, 
              tailored strategy, and measurable growth. You get my full attention.
            </p>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 md:gap-6 lg:gap-8">
          {ingredients.map((item, i) => <motion.article key={i} data-index={i} whileHover={prefersReducedMotion ? undefined : {
          y: -6
        }} transition={{
          duration: 0.5,
          ease: [0.2, 0, 0.2, 1]
        }} className="group flex flex-col justify-between bg-card p-6 md:p-8 border border-border rounded-lg">
              <div className="relative aspect-square w-full overflow-hidden bg-muted rounded-md">
                <motion.img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="h-full w-full object-cover" whileHover={prefersReducedMotion ? undefined : {
              scale: 1.04
            }} transition={{
              duration: 0.5,
              ease: [0.2, 0, 0.2, 1]
            }} />
              </div>

              <div className="mt-6 flex-1 flex flex-col justify-between">
                <div>
                  <span className="font-sans text-[0.875rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    {item.label}
                  </span>
                  <h3 className="mt-3 font-serif font-normal text-foreground" style={{
                fontSize: "1.6875rem",
                letterSpacing: "-0.02em",
                lineHeight: 1.15
              }}>
                    {item.titleStart}
                    <span className="italic">{item.titleItalic}</span>
                    {item.titleEnd}
                  </h3>
                  <p className="mt-3 font-sans text-[1.0625rem] leading-[1.6] text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}